import { Component, ElementRef, EventEmitter, Input, Output, Renderer2, ViewChild } from '@angular/core';
import { Hyperlink } from '@app/core/interfaces/hyperlink';

/**
 * Downloading and uploading files over HTTP with progress indicator on the button.
 *
 * ```html
 * <portal-progress-button
 *  [progress]="progress value binding (undefined ... button is active, 0-100 ... button is disabled and in progress)"
 *  [btnClass]="some bootstrap semantic style class, e.g. btn-primary"
 *  [progressText]="expression providing value that will be shown instead of the spinner icon (optional)"
 *  [disabled]="button disabling"
 *  [isNgbDropdownItem]"if used in dropdown set to `true`"
 *  (clicked)="emits events when the progress button was clicked"
 * >custom HTML here</portal-progress-button>
 * ```
 */
@Component({
  selector: 'portal-progress-button',
  templateUrl: './progress-button.component.html',
  styles: [],
  standalone: false,
})
export class ProgressButtonComponent {
  private inProgress = false;

  constructor(private renderer: Renderer2) {}

  @Input()
  set progress(value: number) {
    if (value != null) {
      this.showProgressBar();
      this.renderer.setStyle(this.progressBarEl.nativeElement, 'width', value + '%');
    } else {
      this.hideProgressBar();
    }
  }

  @Input()
  btnClass: string;

  @Input()
  btnType: 'button' | 'submit' = 'button';

  @Input()
  progressText: string;

  @Input()
  disabled: boolean;

  /**
   * Setting this will render a hyperlink HTML element instead of the button.
   */
  @Input()
  hyperlink: Hyperlink;

  @Input()
  isNgbDropdownItem: boolean = false;

  @Output()
  clicked: EventEmitter<any> = new EventEmitter();

  @ViewChild('progressBar', { read: ElementRef, static: false })
  private progressBarEl: ElementRef;

  @ViewChild('content', { read: ElementRef, static: false })
  private contentEl: ElementRef;

  @ViewChild('spinner', { read: ElementRef, static: false })
  private spinnerEl: ElementRef;

  public emitClick(): void {
    this.clicked.emit();
  }

  private hideProgressBar() {
    if (this.inProgress === true) {
      this.renderer.addClass(this.spinnerEl.nativeElement, 'hidden');
      this.renderer.removeClass(this.contentEl.nativeElement, 'hidden');
      this.renderer.removeClass(this.progressBarEl.nativeElement, 'showProgress');
      this.inProgress = false;
    }
  }

  private showProgressBar() {
    if (this.inProgress === false) {
      this.renderer.removeStyle(this.progressBarEl.nativeElement, 'width');
      this.renderer.addClass(this.progressBarEl.nativeElement, 'showProgress');
      this.renderer.addClass(this.contentEl.nativeElement, 'hidden');
      this.renderer.removeClass(this.spinnerEl.nativeElement, 'hidden');
      this.inProgress = true;
    }
  }
}
